import React, { FC } from 'react';
import { Box, Container, Grid, Theme, Typography } from '@material-ui/core';
import { ReadButton } from 'components/atoms';
import { createStyles, makeStyles } from '@material-ui/styles';
import { LocationIcon, TimeIcon } from 'svgs/careers';
import { ListJobPosition, StyledDivider } from 'components/atoms/careers';
import { CareersForm } from 'containers/forms';
import { SocialMediaSection } from 'components/structures/careers';
import { CareersSingleJob } from 'services/careers/types';
import { displayPublishAt } from 'utils/date';

interface JobPositionProps {
  data: CareersSingleJob;
}

const useStyles = makeStyles((theme: Theme) => {
  return createStyles({
    root: {
      backgroundColor: theme.palette.background.default,
      padding: theme.spacing(7, 3, 18),
      [theme.breakpoints.up('sm')]: {
        padding: theme.spacing(7, 0, 18),
      },
    },
    container: {
      backgroundColor: theme.palette.common.white,
      padding: theme.spacing(6, 5, 7),
      top: 118,
      marginBottom: theme.spacing(7),
      boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.05)',
    },
    title: {
      fontWeight: 600,
      color: theme.palette.primary.dark,
      marginBottom: theme.spacing(3),
    },
    text: {
      fontWeight: 500,
      paddingLeft: theme.spacing(1),
      fontSize: 14,
    },
    slash: {
      fontWeight: 500,
      padding: theme.spacing(0, 1.85),
      [theme.breakpoints.up('md')]: {
        padding: theme.spacing(0, 2),
      },
    },
    subtitle: {
      color: theme.palette.primary.dark,
      paddingBottom: theme.spacing(3),
    },
    message: {
      marginTop: theme.spacing(5),
      [theme.breakpoints.up('sm')]: {
        marginTop: theme.spacing(4),
      },
      '& div': {
        maxHeight: 266,
        [theme.breakpoints.up('sm')]: {
          minHeight: 132,
        },
        '& div': {
          '& textarea': {
            minHeight: 266,
            paddingTop: theme.spacing(2),
            [theme.breakpoints.up('sm')]: {
              minHeight: 132,
            },
            '&:focus': {
              paddingTop: theme.spacing(2),
              maxHeight: 266,
              height: '266px !important',
              [theme.breakpoints.up('sm')]: {
                height: '132px !important',
              },
            },
          },
        },
      },
    },
    columnTitle: {
      color: theme.palette.primary.dark,
      paddingBottom: theme.spacing(3),
    },
    secondBlock: {
      backgroundColor: theme.palette.common.white,
      padding: theme.spacing(4, 3, 3),
      boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.05)',
      [theme.breakpoints.up('sm')]: {
        padding: theme.spacing(5),
      },
    },
    textContainer: {
      margin: 'auto 0px',
    },
    shareVia: {
      fontWeight: 500,
      color: '#7E808F',
    },
  });
});

const JobPosition: FC<JobPositionProps> = (props) => {
  const { data } = props;
  const classes = useStyles();

  return (
    <Box className={classes.root}>
      <Container className={classes.container} fixed>
        <Typography variant="h4" className={classes.title}>
          {data.title}
        </Typography>
        <Grid direction="row" justify="flex-start" container>
          <Grid item>
            <Box display="flex" justifyContent="flex-start" flexDirection="row">
              <LocationIcon />
              <Typography color="textSecondary" className={classes.text}>
                {data.location.name}, {data.location.state.short_name}
              </Typography>
              <Typography color="textSecondary" className={classes.slash}>
                /
              </Typography>
            </Box>
          </Grid>
          <Grid item>
            <Box display="flex" justifyContent="flex-start" flexDirection="row">
              <TimeIcon />
              <Typography color="textSecondary" className={classes.text}>
                {displayPublishAt(data.publish_at)}
              </Typography>
            </Box>
          </Grid>
        </Grid>
        <StyledDivider />
        <Typography color="textSecondary">{data.description}</Typography>
        <Box mb={5} mt={5}>
          <ReadButton text="Apply Now" href={`/careers/jobs/${data.link}#form`} orangeButton />
        </Box>
        <Typography variant="h5" className={classes.subtitle}>
          Position Details
        </Typography>
        <Typography color="textSecondary">{data.details}</Typography>
        <StyledDivider isLargeMargin />
        <Grid container direction="row" justify="space-between" spacing={5}>
          <Grid sm={6} item>
            <Typography variant="h5" className={classes.columnTitle}>
              Responsibilities
            </Typography>
            <ListJobPosition listItems={data.experience.map((value) => value.u1_careers_experience_id.title)} />
          </Grid>
          <Grid sm={6} item>
            <Typography variant="h5" className={classes.columnTitle}>
              Qualifications
            </Typography>
            <ListJobPosition listItems={data.requirements.map((value) => value.u1_careers_requirements_id.title)} />
          </Grid>
          <Box id="form" />
        </Grid>
        <StyledDivider isLargeMargin />
        <Typography variant="h5" className={classes.subtitle}>
          Submit Form
        </Typography>
        <Box mt={3} mb={5}>
          <CareersForm jobTitle={data.title} />
        </Box>
      </Container>
      <Container className={classes.secondBlock} fixed>
        <Grid direction="row" spacing={3} container>
          <Grid item className={classes.textContainer}>
            <Typography variant="h6" className={classes.shareVia}>
              Share Via:
            </Typography>
          </Grid>
          <Grid item>
            <SocialMediaSection />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default JobPosition;
